import { ES_PLANS } from "@/lib/es-insurance-va";

/** English column labels, used whenever a Spanish label is missing. */
const EN_HEADINGS = [
  "Plan",
  "BMI required",
  "Lifestyle requirement",
  "Must try another drug first?",
  "Initial approval",
  "Renewal",
];

const ES_HEADINGS = [
  "Plan",
  "IMC requerido",
  "Requisito de estilo de vida",
  "¿Debe probar otro medicamento primero?",
  "Aprobación inicial",
  "Renovación",
];

const HEADINGS = EN_HEADINGS.map((en, i) =>
  ES_HEADINGS[i]?.trim() ? ES_HEADINGS[i] : en,
);

/** Stacked-card labels: Spanish first, English fallback. */
const MOBILE_LABELS = {
  bmi: HEADINGS[1],
  lifestyle: ES_HEADINGS[2]?.trim() ? "Estilo de vida" : "Lifestyle",
  step: ES_HEADINGS[3]?.trim() ? "Terapia escalonada" : "Step therapy",
  initial: HEADINGS[4],
  renewal: HEADINGS[5],
};

/** Cell text plus the optional clarifying note, shown inline in Spanish. */
function Cell({ value, note }: { value: string; note?: string }) {
  return (
    <>
      <span>{value}</span>
      {note ? (
        <small className="mt-1 block text-xs text-muted-foreground">{note}</small>
      ) : null}
    </>
  );
}

/**
 * Spanish plan-by-plan coverage grid: a real table on desktop, stacked
 * definition cards on phones so nothing overflows at 360px.
 */
export function EsCoverageTable() {
  return (
    <>
      <div className="mt-4 hidden overflow-x-auto md:block">
        <table className="w-full border-collapse text-sm">
          <caption className="sr-only">
            Criterios de autorización previa para medicamentos de pérdida de peso, por plan en
            Virginia
          </caption>
          <thead>
            <tr className="bg-brand-soft/60 text-left">
              {HEADINGS.map((h) => (
                <th key={h} scope="col" className="border border-border p-2.5">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {ES_PLANS.map((p) => (
              <tr key={p.plan} className="align-top">
                <th scope="row" className="border border-border p-2.5 text-left font-medium">
                  {p.plan}
                </th>
                <td className="border border-border p-2.5">
                  <Cell value={p.bmi} note={p.bmiNote} />
                </td>
                <td className="border border-border p-2.5">
                  <Cell value={p.lifestyle} note={p.lifestyleNote} />
                </td>
                <td className="border border-border p-2.5">
                  <Cell value={p.step} note={p.stepNote} />
                </td>
                <td className="border border-border p-2.5">
                  <Cell value={p.initial} note={p.initialNote} />
                </td>
                <td className="border border-border p-2.5">
                  <Cell value={p.renewal} note={p.renewalNote} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-4 space-y-3 md:hidden">
        {ES_PLANS.map((p) => (
          <div key={p.plan} className="rounded border border-border p-3 text-sm">
            <h3 className="mb-2 text-base font-medium text-brand-deep">{p.plan}</h3>
            <dl className="space-y-1.5">
              <dt className="text-muted-foreground">{MOBILE_LABELS.bmi}</dt>
              <dd>
                <Cell value={p.bmi} note={p.bmiNote} />
              </dd>
              <dt className="text-muted-foreground">{MOBILE_LABELS.lifestyle}</dt>
              <dd>
                <Cell value={p.lifestyle} note={p.lifestyleNote} />
              </dd>
              <dt className="text-muted-foreground">{MOBILE_LABELS.step}</dt>
              <dd>
                <Cell value={p.step} note={p.stepNote} />
              </dd>
              <dt className="text-muted-foreground">{MOBILE_LABELS.initial}</dt>
              <dd>
                <Cell value={p.initial} note={p.initialNote} />
              </dd>
              <dt className="text-muted-foreground">{MOBILE_LABELS.renewal}</dt>
              <dd>
                <Cell value={p.renewal} note={p.renewalNote} />
              </dd>
            </dl>
          </div>
        ))}
      </div>
    </>
  );
}
